// Centralized, typed API endpoint functions. Pages call these via TanStack Query.
import type {
  Brand,
  ContactCallPrep,
  Conversation,
  ConversationAiAnalysis,
  ConversationMessage,
  ConversationNote,
  CrmContact,
  PhoneNumberMapping,
  ResellerSite,
  Seller,
  User,
  Channel,
  Direction,
  ConversationStatus,
} from '@tips-crm/shared';
import { apiDelete, apiGet, apiPatch, apiPost } from './client.js';

// ---- Auth ------------------------------------------------------------------
export interface LoginResult {
  token: string;
  user: User;
}

export function login(email: string, password: string): Promise<LoginResult> {
  return apiPost<LoginResult>('/auth/login', { email, password });
}

export function fetchCurrentUser(): Promise<User> {
  return apiGet<User>('/auth/me');
}

export function forgotPassword(email: string): Promise<void> {
  return apiPost<void>('/auth/forgot-password', { email });
}

// ---- API keys (MCP / programmatic access) ----------------------------------
export interface ApiKey {
  id: string;
  name: string;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
}
export interface CreatedApiKey {
  id: string;
  name: string;
  key: string; // raw key — returned only once, at creation
  created_at: string;
}
export function listApiKeys(): Promise<ApiKey[]> {
  return apiGet<ApiKey[]>('/api-keys');
}
export function createApiKey(name: string): Promise<CreatedApiKey> {
  return apiPost<CreatedApiKey>('/api-keys', { name });
}
export function revokeApiKey(id: string): Promise<{ id: string }> {
  return apiDelete<{ id: string }>(`/api-keys/${id}`);
}

export function resetPassword(token: string, password: string): Promise<void> {
  return apiPost<void>('/auth/reset-password', { token, password });
}

// ---- Reports ---------------------------------------------------------------
export interface ReportOutcome {
  disposition: string;
  count: number;
}

export interface ReportSummary {
  inbound_calls: number;
  outbound_calls: number;
  inbound_chats: number;
  outbound_chats: number;
  missed: number;
  abandoned: number;
  avg_handle_time_seconds: number;
  summary_coverage_pct: number;
  total_conversations: number;
  outcomes: ReportOutcome[];
}

export function fetchReportSummary(params?: {
  from?: string;
  to?: string;
  seller_id?: string;
}): Promise<ReportSummary> {
  return apiGet<ReportSummary>('/reports/summary', { params });
}

// ---- Lead distribution -----------------------------------------------------
export interface RepPerformance {
  user_id: string;
  display_name: string;
  email: string;
  role: string;
  seller_id: string | null;
  seller_name: string | null;
  total_calls: number;
  completed: number;
  abandoned: number;
  dispositioned: number;
  avg_handle_seconds: number;
  open_leads: number;
  // Performance score used to suggest a weighted lead split (completed calls).
  score: number;
  // AI coaching aggregates — null/0 until calls have analyses.
  ai_analyzed_calls: number;
  avg_discovery: number | null;
  avg_objection_handling: number | null;
  avg_closing: number | null;
  avg_next_steps: number | null;
  avg_talk_ratio: number | null;
  objection_count: number;
  objections_well_handled: number;
  competitor_mentions: number;
}

export interface RepPerformanceReport {
  reps: RepPerformance[];
  unassigned_pool: number;
}

export function fetchRepPerformance(params?: {
  from?: string;
  to?: string;
}): Promise<RepPerformanceReport> {
  return apiGet<RepPerformanceReport>('/reports/rep-performance', { params });
}

export interface DistributionResult {
  assignments: { user_id: string; requested: number; assigned: number }[];
  total_requested: number;
  total_assigned: number;
  pool_exhausted: boolean;
}

export function distributeLeads(
  assignments: { user_id: string; count: number }[],
): Promise<DistributionResult> {
  return apiPost<DistributionResult>('/contacts/distribute', { assignments });
}

// ---- Contacts --------------------------------------------------------------
export interface Paginated<T> {
  items: T[];
  total: number;
}

export type ContactSort =
  | 'name'
  | 'email'
  | 'phone'
  | 'company'
  | 'lifecycle_status'
  | 'created_at'
  | 'updated_at';

export function fetchContacts(params?: {
  q?: string;
  seller_id?: string;
  // Per-column header filters (partial, case-insensitive).
  name?: string;
  email?: string;
  phone?: string;
  company?: string;
  lifecycle_status?: string;
  // Admin-facing: view a specific rep's book of leads.
  assigned_user_id?: string;
  // Sorting.
  sort?: ContactSort;
  dir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}): Promise<Paginated<CrmContact>> {
  return apiGet<Paginated<CrmContact>>('/contacts', { params });
}

// Sales reps an admin can allocate leads to (GET /users, admin-only).
export interface AssignableUser {
  id: string;
  display_name: string;
  email: string;
  role: User['role'];
  seller_id: string | null;
}

export function fetchAssignableUsers(): Promise<AssignableUser[]> {
  return apiGet<AssignableUser[]>('/users');
}

// Set (or clear) the seller a rep belongs to (platform-admin only). Reps can only
// receive leads that belong to their seller.
export function setUserSeller(id: string, seller_id: string | null): Promise<AssignableUser> {
  return apiPatch<AssignableUser>(`/users/${id}/seller`, { seller_id });
}

export function assignContact(
  id: string,
  assigned_user_id: string | null,
): Promise<CrmContact> {
  return apiPatch<CrmContact>(`/contacts/${id}/assign`, { assigned_user_id });
}

export function fetchContact(id: string): Promise<CrmContact> {
  return apiGet<CrmContact>(`/contacts/${id}`);
}

export function fetchContactConversations(id: string): Promise<Conversation[]> {
  return apiGet<Conversation[]>(`/contacts/${id}/conversations`);
}

// ---- Conversations ---------------------------------------------------------
export interface ConversationFilters {
  q?: string;
  channel?: Channel;
  direction?: Direction;
  status?: ConversationStatus;
  from?: string;
  to?: string;
  seller_id?: string;
  limit?: number;
  offset?: number;
}

// The list endpoint enriches each row with the linked contact's display name + email
// (so name/email search results are legible). Detail fetches return a plain Conversation.
export interface ConversationListItem extends Conversation {
  contact_name?: string | null;
  contact_email?: string | null;
}

export function fetchConversations(
  filters?: ConversationFilters,
): Promise<Paginated<ConversationListItem>> {
  return apiGet<Paginated<ConversationListItem>>('/conversations', { params: filters });
}

export function fetchConversation(id: string): Promise<Conversation> {
  return apiGet<Conversation>(`/conversations/${id}`);
}

// Resolve a conversation by its Amazon Connect contact id (agent workspace linking).
export function fetchConversationByConnectContact(connectContactId: string): Promise<Conversation> {
  return apiGet<Conversation>(
    `/conversations/by-connect-contact/${encodeURIComponent(connectContactId)}`,
  );
}

export function fetchConversationMessages(id: string): Promise<ConversationMessage[]> {
  return apiGet<ConversationMessage[]>(`/conversations/${id}/messages`);
}

// --- Live agent chat (reply to a website visitor from the CRM) ---
export interface AgentChatMessage {
  id?: string;
  sender: 'customer' | 'agent' | 'system';
  displayName: string | null;
  body: string;
  at: string;
}
export interface AgentChatSession {
  displayName: string;
  messages: AgentChatMessage[];
}
export function startChatAgentSession(id: string): Promise<AgentChatSession> {
  return apiPost<AgentChatSession>(`/conversations/${id}/chat/agent-session`, {});
}
export function sendChatMessage(id: string, content: string): Promise<{ sent: boolean; at: string }> {
  return apiPost<{ sent: boolean; at: string }>(`/conversations/${id}/chat/send`, { content });
}
export function endChat(id: string): Promise<{ ended: boolean }> {
  return apiPost<{ ended: boolean }>(`/conversations/${id}/chat/end`, {});
}

export function fetchConversationNotes(id: string): Promise<ConversationNote[]> {
  return apiGet<ConversationNote[]>(`/conversations/${id}/notes`);
}

export interface ConversationArtifact {
  id: string;
  conversation_id: string;
  artifact_type: string;
  url: string | null;
  created_at: string;
}

export function fetchConversationArtifacts(id: string): Promise<ConversationArtifact[]> {
  return apiGet<ConversationArtifact[]>(`/conversations/${id}/artifacts`);
}

export interface RecordingUrl {
  url: string;
  expires_in: number;
}
export function fetchRecordingUrl(id: string): Promise<RecordingUrl> {
  return apiGet<RecordingUrl>(`/conversations/${id}/recording-url`);
}

export function addConversationNote(
  conversationId: string,
  body: string,
): Promise<ConversationNote> {
  return apiPost<ConversationNote>(`/conversations/${conversationId}/notes`, { body });
}

export function setDisposition(
  conversationId: string,
  disposition: string,
  note?: string,
): Promise<Conversation> {
  return apiPatch<Conversation>(`/conversations/${conversationId}/disposition`, {
    disposition,
    note,
  });
}

// ---- AI call analysis --------------------------------------------------------
// 404 until the worker produces a row — callers poll (like fetchRecordingUrl).
export function fetchConversationAnalysis(id: string): Promise<ConversationAiAnalysis> {
  return apiGet<ConversationAiAnalysis>(`/conversations/${id}/analysis`);
}

export function rerunConversationAnalysis(id: string): Promise<{ queued: boolean }> {
  return apiPost<{ queued: boolean }>(`/conversations/${id}/analysis/rerun`);
}

export function fetchContactCallPrep(contactId: string): Promise<ContactCallPrep> {
  return apiGet<ContactCallPrep>(`/contacts/${contactId}/call-prep`);
}

// ---- Click-to-call --------------------------------------------------------
// Places an attributed outbound call: creates a conversation row owned by the
// calling rep + linked to the contact, then triggers Amazon Connect. Dialing the
// contact's own number (destination omitted) is the common case.
export interface OutboundCallResult {
  conversation: Conversation;
  connect: { dormant: boolean; contactId: string };
}
export function startOutboundCall(params: {
  contact_id: string;
  destination_phone_number?: string;
  brand_id?: string | null;
}): Promise<OutboundCallResult> {
  return apiPost<OutboundCallResult>('/conversations/outbound', params);
}

// ---- Admin: Sellers --------------------------------------------------------
export function fetchSellers(): Promise<Seller[]> {
  return apiGet<Seller[]>('/sellers');
}
export function createSeller(input: { name: string; status?: string }): Promise<Seller> {
  return apiPost<Seller>('/sellers', input);
}
export function updateSeller(id: string, input: Partial<Seller>): Promise<Seller> {
  return apiPatch<Seller>(`/sellers/${id}`, input);
}
export function deleteSeller(id: string): Promise<{ id: string }> {
  return apiDelete<{ id: string }>(`/sellers/${id}`);
}

// ---- Admin: Brands ---------------------------------------------------------
export function fetchBrands(sellerId?: string): Promise<Brand[]> {
  return apiGet<Brand[]>('/brands', { params: sellerId ? { seller_id: sellerId } : undefined });
}
export function createBrand(input: Partial<Brand> & { seller_id: string; name: string }): Promise<Brand> {
  return apiPost<Brand>('/brands', input);
}
export function updateBrand(id: string, input: Partial<Brand>): Promise<Brand> {
  return apiPatch<Brand>(`/brands/${id}`, input);
}
export function deleteBrand(id: string): Promise<{ id: string }> {
  return apiDelete<{ id: string }>(`/brands/${id}`);
}

// ---- Admin: Sites ----------------------------------------------------------
export function fetchSites(sellerId?: string): Promise<ResellerSite[]> {
  return apiGet<ResellerSite[]>('/sites', { params: sellerId ? { seller_id: sellerId } : undefined });
}
export function createSite(
  input: Partial<ResellerSite> & { seller_id: string; brand_id: string; domain: string },
): Promise<ResellerSite> {
  return apiPost<ResellerSite>('/sites', input);
}
export function updateSite(id: string, input: Partial<ResellerSite>): Promise<ResellerSite> {
  return apiPatch<ResellerSite>(`/sites/${id}`, input);
}
export function deleteSite(id: string): Promise<{ id: string }> {
  return apiDelete<{ id: string }>(`/sites/${id}`);
}
export interface WidgetTokenResult {
  token: string;
  widget_public_key: string;
}
export function fetchSiteWidgetToken(id: string): Promise<WidgetTokenResult> {
  return apiGet<WidgetTokenResult>(`/sites/${id}/widget-token`);
}

// ---- Admin: Phone numbers --------------------------------------------------
export function fetchPhoneNumbers(sellerId?: string): Promise<PhoneNumberMapping[]> {
  return apiGet<PhoneNumberMapping[]>('/phone-numbers', {
    params: sellerId ? { seller_id: sellerId } : undefined,
  });
}
export function createPhoneNumber(
  input: Partial<PhoneNumberMapping> & { seller_id: string; phone_number_e164: string },
): Promise<PhoneNumberMapping> {
  return apiPost<PhoneNumberMapping>('/phone-numbers', input);
}
export function updatePhoneNumber(
  id: string,
  input: Partial<PhoneNumberMapping>,
): Promise<PhoneNumberMapping> {
  return apiPatch<PhoneNumberMapping>(`/phone-numbers/${id}`, input);
}
export function deletePhoneNumber(id: string): Promise<{ id: string }> {
  return apiDelete<{ id: string }>(`/phone-numbers/${id}`);
}
